// Core Router - Route Constants
// Location: src/core/router/routes.ts

import type { UserRole } from '../../types/auth.types';
import { resolveProviderRoute } from './resolveProviderRoute';
import { useProviderStore } from '../store/providerStore';

export const ROUTES = {
  HOME: '/',
  ABOUT: '/about',
  CONTACT: '/contact',
  PRIVACY: '/privacy-policy',
  TERMS: '/terms-of-service',

  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  CUSTOMER: {
    DASHBOARD: '/customer/dashboard',
    CREATE_DELIVERY: '/customer/create-delivery',
    DELIVERIES: '/customer/deliveries',
    TRACK_DELIVERY: (id: string) => `/customer/track/${id}`,
    DELIVERY_DETAILS: (id: string) => `/customer/deliveries/${id}`,
    ORDER_DETAIL: (id: string) => `/customer/orders/${id}`,
    ORDER_HISTORY: '/customer/orders',
    ADDRESSES: '/customer/addresses',
  },

  PROVIDER: {
    DASHBOARD: '/provider/dashboard',
    ORG_ADMIN: '/provider/org',
    ORG_DRIVER_LICENCES: (userId: string) => `/provider/org/drivers/${userId}/licences`,
    ACTIVE_DELIVERIES: '/provider/deliveries/active',
    DELIVERY_DETAILS: (id: string) => `/provider/deliveries/${id}`,
    EARNINGS: '/provider/earnings',
    HISTORY: '/provider/history',
  },

  ONBOARDING: {
    SETUP: '/onboarding',
    VERIFY: (vehicleId: string) => `/onboarding/vehicle/${vehicleId}/verify`,
    LICENCE: '/onboarding/licence',
  },

  INVITE: {
    ACCEPT: '/invite/accept',
  },

  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: (userId: string) => `/admin/users/${userId}`,
    VEHICLES: '/admin/vehicles',
    DRIVERS: '/admin/drivers',
    DELIVERIES: '/admin/deliveries',
    ANALYTICS: '/admin/analytics',
  },

  ACCOUNT: '/account',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  NOTIFICATIONS: '/notifications',

  DELIVERY_PAGE: '/deliverypage',

  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  ERROR: '/error',
} as const;

export const USER_ROLES: Record<string, UserRole> = {
  CUSTOMER: 'customer',
  PROVIDER: 'provider',
  ADMIN: 'admin',
} as const;

export const getDashboardRoute = (role: UserRole): string => {
  switch (role) {
    case USER_ROLES.CUSTOMER:
      return ROUTES.CUSTOMER.DASHBOARD;
    case USER_ROLES.PROVIDER: {
      const { vehicles } = useProviderStore.getState();
      return resolveProviderRoute(role, vehicles);
    }
    case USER_ROLES.ADMIN:
      return ROUTES.ADMIN.USERS;
    default:
      return ROUTES.HOME;
  }
};

/** Async provider landing after login when vehicles may not be loaded yet */
export const getProviderLandingRoute = async (): Promise<string> => {
  const state = useProviderStore.getState();
  const vehicles =
    state.vehicles.length > 0
      ? state.vehicles
      : await state.refreshVehicles();
  return resolveProviderRoute('provider', vehicles);
};
